import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import socket from "../../../utils/socket.js";
import { apiGet, apiPost, apiPut } from "../../../utils/api.js";
import { ArrowLeft, Monitor, Database, Clock, HardDrive, RefreshCw, AlertCircle, UploadCloud, FileText, Terminal, ChevronDown, ChevronUp, CheckCircle2 } from "lucide-react";
import "./deviceControl.css";

const UPDATE_IDLE_STATES = new Set(["idle", "no_update", "success", "failed", "rollback_success", "rollback_failed", undefined]);

function updateStateLabel(state) {
  const labels = {
    checking: "Checking…", update_available: "Found…",
    downloading: "Downloading…", verifying: "Verifying…",
    staging: "Staging…", installing: "Installing…",
    restarting: "Restarting…", already_running: "In Progress",
  };
  return labels[state] ?? null;
}

const bytesToGB = (bytes) => (bytes / 1024 ** 3).toFixed(2);

const DeviceDetail = () => {
  const { id } = useParams();
  const [device, setDevice] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [licenseStatus, setLicenseStatus] = useState(null); // null = checking, { isLicensed, isBlocked, hostname }
  const [agentInfo, setAgentInfo] = useState(null); // { needsUpdate, latestVersion, status }
  const [updateState, setUpdateState] = useState(undefined);
  const [diagOpen, setDiagOpen] = useState(false);
  const [diagTab, setDiagTab] = useState("env"); // "env" | "log"
  const [diag, setDiag] = useState(null);
  const [diagLoading, setDiagLoading] = useState(false);
  const [diagEditing, setDiagEditing] = useState(false);
  const [diagDraft, setDiagDraft] = useState("");
  const [diagSaving, setDiagSaving] = useState(false);
  const [diagRestartAfter, setDiagRestartAfter] = useState(true);
  const [diagMsg, setDiagMsg] = useState(null); // { type: "ok"|"err", text }
  const [updateError, setUpdateError] = useState(null);
  const [socketConnected, setSocketConnected] = useState(null); // null=checking, true/false=known
  const navigate = useNavigate();

  useEffect(() => {
    if (!id) return;

    // Check license before loading any telemetry data.
    // If the device is not licensed, show the approval notice instead.
    apiGet(`/api/agents/${id}/license`)
      .then(async (r) => {
        if (r.status === 404) {
          setError("Agent not found.");
          setLoading(false);
          return null;
        }
        return r.json();
      })
      .then((data) => {
        if (!data) return;
        setLicenseStatus(data);
        if (!data.isLicensed || data.isBlocked) {
          setLoading(false);
          return;
        }
        // Licensed — proceed to load system telemetry
        socket.emit("get_data", { type: "system_info", agentId: id }, (res) => {
          if (res?.success) {
            const raw = Array.isArray(res.data) ? res.data[0] : res.data;
            setDevice(raw?.data || null);
            setError(null);
          } else {
            setError("Failed to retrieve system telemetrics.");
          }
          setLoading(false);
        });
      })
      .catch(() => {
        // If the license check itself fails, fall through to normal load
        setLicenseStatus({ isLicensed: true, isBlocked: false });
        socket.emit("get_data", { type: "system_info", agentId: id }, (res) => {
          if (res?.success) {
            const raw = Array.isArray(res.data) ? res.data[0] : res.data;
            setDevice(raw?.data || null);
          } else {
            setError("Failed to retrieve system telemetrics.");
          }
          setLoading(false);
        });
      });

    apiGet("/api/visualizer-data")
      .then((r) => r.json())
      .then((data) => {
        if (Array.isArray(data)) {
          const found = data.find((a) => a.agentId === id);
          if (found) setAgentInfo(found);
        }
      })
      .catch(() => {});

    const handleUpdateStatus = (data) => {
      if (data?.agentId === id) setUpdateState(data.state);
    };
    socket.on("agent_update_status", handleUpdateStatus);
    return () => socket.off("agent_update_status", handleUpdateStatus);
  }, [id]);

  // Poll the real socket connectivity (separate from DB status, which can be stale)
  useEffect(() => {
    if (!id) return;
    let cancelled = false;
    const check = () => {
      apiGet(`/api/agents/${id}/socket-status`)
        .then((r) => r.json())
        .then((d) => { if (!cancelled) setSocketConnected(!!d?.connected); })
        .catch(() => { if (!cancelled) setSocketConnected(false); });
    };
    check();
    const t = setInterval(check, 15_000);
    return () => { cancelled = true; clearInterval(t); };
  }, [id]);

  const fetchDiag = () => {
    setDiag(null);
    setDiagMsg(null);
    setDiagEditing(false);
    setDiagLoading(true);
    apiGet(`/api/agents/${id}/diagnostics`)
      .then((r) => {
        if (!r.ok) return r.json().then((e) => Promise.reject(e?.error || "Server error"));
        return r.json();
      })
      .then((data) => {
        setDiag(data);
        setDiagLoading(false);
        if (data?.fromCache) {
          const when = data.cachedAt ? new Date(data.cachedAt).toLocaleString() : "unknown time";
          setDiagMsg({ type: "ok", text: `Showing cached snapshot from ${when} (agent offline — .env editing disabled).` });
        }
      })
      .catch((errMsg) => {
        setDiagMsg({ type: "err", text: typeof errMsg === "string" ? errMsg : "Failed to pull diagnostics — agent may be offline." });
        setDiagLoading(false);
      });
  };

  const toggleDiag = () => {
    if (diagOpen) { setDiagOpen(false); return; }
    setDiagOpen(true);
    if (diag) return; // already have valid data; use refresh button to force re-fetch
    fetchDiag();
  };

  const parseEnvMap = (text = "") => {
    const map = {};
    for (const line of text.split("\n")) {
      const t = line.trim();
      if (!t || t.startsWith("#")) continue;
      const eq = t.indexOf("=");
      if (eq === -1) continue;
      map[t.slice(0, eq).trim()] = t.slice(eq + 1);
    }
    return map;
  };

  const startEdit = () => {
    setDiagDraft(diag?.envContent || "");
    setDiagMsg(null);
    setDiagEditing(true);
  };

  const cancelEdit = () => { setDiagEditing(false); setDiagMsg(null); };

  const saveEnv = async () => {
    setDiagSaving(true);
    setDiagMsg(null);
    try {
      const r = await apiPut(`/api/agents/${id}/diagnostics/env`, { envContent: diagDraft, restartAfter: diagRestartAfter });
      const data = await r.json();
      if (!r.ok) { setDiagMsg({ type: "err", text: data.error || "Save failed" }); return; }
      // Refresh cached diagnostics so the view shows the new content
      setDiagEditing(false);
      if (diagRestartAfter) {
        // Agent is restarting — clear cached diag so next open re-fetches fresh data
        setDiag(null);
        setDiagMsg({ type: "ok", text: "Config saved. Agent is restarting — it will reconnect in a few seconds." });
      } else {
        setDiag((prev) => ({ ...prev, envContent: diagDraft }));
        setDiagMsg({ type: "ok", text: "Config saved. Restart the agent to apply changes." });
      }
    } catch {
      setDiagMsg({ type: "err", text: "Network error — config was not saved." });
    } finally {
      setDiagSaving(false);
    }
  };

  // Compute changed keys for inline warnings (diff between saved and draft)
  const envDiff = (() => {
    if (!diagEditing || !diag?.envContent) return { changed: [], added: [], removed: [] };
    const orig = parseEnvMap(diag.envContent);
    const next = parseEnvMap(diagDraft);
    const allKeys = new Set([...Object.keys(orig), ...Object.keys(next)]);
    const changed = [], added = [], removed = [];
    for (const k of allKeys) {
      if (!(k in orig)) added.push(k);
      else if (!(k in next)) removed.push(k);
      else if (orig[k] !== next[k]) changed.push(k);
    }
    return { changed, added, removed };
  })();

  const SENSITIVE_KEYS = new Set(["AGENT_ID", "TENANT_KEY"]);
  const hasSensitiveChange = [...envDiff.changed, ...envDiff.removed].some((k) => SENSITIVE_KEYS.has(k));

  const canUpdate = agentInfo?.needsUpdate && agentInfo?.status === "online" && UPDATE_IDLE_STATES.has(updateState);
  const uLabel = updateStateLabel(updateState);

  const handleUpdate = () => {
    if (!canUpdate) return;
    setUpdateError(null);
    apiPost(`/api/agents/${id}/update-now`, {})
      .then((r) => r.json())
      .then((data) => { if (!data.success) setUpdateError(data.error || "Update request failed"); })
      .catch(() => setUpdateError("Agent is offline or unreachable"));
  };

  if (loading) return (
    <div className="pc-container" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100vh', flexDirection: 'column', gap: '20px' }}>
      <RefreshCw size={40} className="animate-spin" style={{ color: '#00b4d8' }} />
      <p style={{ color: '#8ca8b3' }}>Synchronizing local node data...</p>
    </div>
  );

  // Device is blocked — it has been removed by the administrator
  if (licenseStatus?.isBlocked) return (
    <div className="pc-container" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100vh', flexDirection: 'column', gap: '16px' }}>
      <AlertCircle size={52} style={{ color: '#ef4444' }} />
      <p style={{ color: '#ef4444', fontWeight: 700, fontSize: '1.1rem', margin: 0 }}>Device Blocked</p>
      <p style={{ color: '#8ca8b3', margin: 0, textAlign: 'center', maxWidth: 380 }}>
        This device has been blocked by an administrator and is no longer active. Contact your administrator to unblock it.
      </p>
      <button className="gc-icon-btn" style={{ marginTop: 8 }} onClick={() => navigate("/devices")} data-tip="Back to Devices" aria-label="Back to Devices"><ArrowLeft size={16} /></button>
    </div>
  );

  // Device is registered but license has not been approved yet
  if (licenseStatus && !licenseStatus.isLicensed) return (
    <div className="pc-container" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100vh', flexDirection: 'column', gap: '16px' }}>
      <AlertCircle size={52} style={{ color: '#f59e0b' }} />
      <p style={{ color: '#f59e0b', fontWeight: 700, fontSize: '1.1rem', margin: 0 }}>License Not Approved</p>
      <p style={{ color: '#8ca8b3', margin: 0, textAlign: 'center', maxWidth: 420 }}>
        This device is registered but its license has not been approved yet.
        An administrator must approve the license in the <strong>License Manager</strong> before you can access its telemetry.
      </p>
      <button className="gc-icon-btn" style={{ marginTop: 8 }} onClick={() => navigate("/devices")} data-tip="Back to Devices" aria-label="Back to Devices"><ArrowLeft size={16} /></button>
    </div>
  );

  if (error || !device) return (
    <div className="pc-container" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100vh', flexDirection: 'column', gap: '20px' }}>
      <AlertCircle size={48} style={{ color: '#ef4444' }} />
      <p style={{ color: '#ef4444', fontWeight: 'bold' }}>{error || "Node Unreachable"}</p>
      <button className="gc-icon-btn" onClick={() => navigate("/devices")} data-tip="Back to Devices" aria-label="Back to Devices"><ArrowLeft size={16} /></button>
    </div>
  );

  const cpu = device.cpu || {};
  const memory = device.memory || {};
  const disk = device.disk || {};
  const collected = device.timestamp || new Date().toISOString();

  return (
    <div className="pc-container">
      <div className="pc-header">
        <button className="gc-icon-btn" onClick={() => navigate("/devices")} data-tip="Back to Devices" aria-label="Back to Devices">
          <ArrowLeft size={16} />
        </button>
        <div className="pc-title-group">
          <h1 className="pc-title">{device.hostname || "Unknown Node"}</h1>
          <p className="pc-subtitle">Hardware Identification: {device.machine_id}</p>
          {socketConnected !== null && (
            <span
              className={`dc-socket-badge ${socketConnected ? "dc-socket-badge--live" : "dc-socket-badge--dead"}`}
              title={socketConnected
                ? "Agent socket is live — screen viewing and diagnostics are available"
                : "Agent socket is disconnected — screen viewing and diagnostics are unavailable even if status shows Online. Restart the agent to reconnect."}
            >
              <span className="dc-socket-dot" />
              {socketConnected ? "Socket Live" : "Socket Disconnected"}
            </span>
          )}
        </div>
        <div className="dc-update-group">
          {(agentInfo?.needsUpdate || uLabel) && agentInfo?.status === "online" && (
            <button
              className={`dc-update-btn ${!canUpdate ? "dc-update-btn--busy" : ""}`}
              onClick={handleUpdate}
              title={agentInfo?.latestVersion ? `Update to v${agentInfo.latestVersion}` : "Update available"}
            >
              <UploadCloud size={15} />
              {uLabel ?? `Update to v${agentInfo?.latestVersion ?? "latest"}`}
            </button>
          )}
          {updateError && (
            <span className="dc-update-error"><AlertCircle size={13} /> {updateError}</span>
          )}
        </div>
      </div>

      <div className="pc-section">
        <div className="section-title">
          <Monitor size={20} /> System Architecture
        </div>
        <div className="system-info-grid">
          <div className="info-item">
            <span className="info-label">OS Environment</span>
            <span className="info-value">{device.os_type} {device.os_version} (Release {device.os_release})</span>
          </div>
          <div className="info-item">
            <span className="info-label">Processing Power</span>
            <span className="info-value">{cpu.physical_cores} Cores / {cpu.logical_cores} Threads @ {cpu.cpu_freq_mhz} MHz</span>
          </div>
          <div className="info-item">
            <span className="info-label">Memory Utilization</span>
            <span className="info-value">{bytesToGB(memory.used_ram)} / {bytesToGB(memory.total_ram)} GB ({memory.ram_percent}%)</span>
            <div className="progress-container">
              <div className="pc-progress-bar">
                <div className="progress-bar-fill memory-fill" style={{ width: `${memory.ram_percent || 0}%` }}></div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="pc-section">
        <div className="section-title">
          <Database size={20} /> Storage & Volumes
        </div>
        <div className="drives-grid">
          {Object.keys(disk).map((drive) => {
            const d = disk[drive];
            const p = d.percent || 0;
            let statusClass = "";
            if (p > 90) statusClass = "danger";
            else if (p > 75) statusClass = "warning";

            return (
              <div key={drive} className="drive-card">
                <div className="drive-header">
                  <div className="drive-name"><HardDrive size={18} style={{ color: '#00b4d8' }} /> {drive}</div>
                  <div className="drive-size">{p}% Full</div>
                </div>
                <div className="progress-container">
                  <div className="progress-labels">
                    <span>{bytesToGB(d.used)} GB Used</span>
                    <span>{bytesToGB(d.total)} GB Total</span>
                  </div>
                  <div className="pc-progress-bar">
                    <div className={`progress-bar-fill disk-fill ${statusClass}`} style={{ width: `${p}%` }}></div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      <div className="collected-badge">
        <Clock size={14} />
        <span>Last synchronization: {new Date(collected).toLocaleString()}</span>
      </div>

      {/* ── Diagnostics ── */}
      <div className="pc-section diag-section">
        <div className="diag-toggle-row">
          <button className="diag-toggle" onClick={toggleDiag}>
            <span className="diag-toggle-left">
              <Terminal size={18} />
              Agent Diagnostics
            </span>
            {diagOpen ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
          </button>
          {diagOpen && (
            <button className="gc-icon-btn" onClick={fetchDiag} data-tip="Refresh" title="Refresh diagnostics from agent">
              <RefreshCw size={14} />
            </button>
          )}
        </div>

        {diagOpen && (
          <div className="diag-body">
            {socketConnected === false && (
              <div className="diag-socket-dead">
                <AlertCircle size={16} />
                <div>
                  <strong>Agent socket is disconnected</strong>
                  <p>
                    The agent process is running but its connection to the backend is broken.
                    This is usually caused by a wrong <code>SERVER_URL</code> or <code>TENANT_KEY</code> in the agent's <code>.env</code> file, or the backend restarted and the agent failed to reconnect.
                  </p>
                  <p>
                    <strong>To fix:</strong> Go to the agent machine, open <code>agent_current\.env</code>, verify <code>SERVER_URL</code> points to this server, then restart the agent process. Once it reconnects, this panel will work.
                  </p>
                </div>
              </div>
            )}

            {diagLoading && (
              <div className="diag-loading">
                <RefreshCw size={18} className="animate-spin" /> Pulling files from agent…
              </div>
            )}

            {/* Status message — shown regardless of whether diag data is loaded */}
            {!diagLoading && diagMsg && (
              <div className={`diag-status-msg ${diagMsg.type === "ok" ? "diag-status-ok" : "diag-status-err"}`}>
                {diagMsg.type === "ok" ? "✓" : "✕"} {diagMsg.text}
              </div>
            )}

            {!diagLoading && diag && (
              <>
                {diag.lastUpdateResult?.reportedAt && (
                  <div className={`diag-update-result ${diag.lastUpdateResult.success ? "is-ok" : "is-fail"}`}>
                    {diag.lastUpdateResult.success ? <CheckCircle2 size={16} /> : <AlertCircle size={16} />}
                    <div>
                      <strong>{diag.lastUpdateResult.success ? "Last update succeeded" : "Last update failed — automatically rolled back"}</strong>
                      <p>
                        {diag.lastUpdateResult.fromVersion || "?"} → {diag.lastUpdateResult.toVersion || "?"}
                        {diag.lastUpdateResult.reason && ` — ${diag.lastUpdateResult.reason}`}
                        {" "}({new Date(diag.lastUpdateResult.reportedAt).toLocaleString()})
                      </p>
                    </div>
                  </div>
                )}

                <div className="diag-tabs">
                  <button
                    className={`diag-tab${diagTab === "env" ? " active" : ""}`}
                    onClick={() => setDiagTab("env")}
                  >
                    <FileText size={14} /> .env Config
                  </button>
                  <button
                    className={`diag-tab${diagTab === "log" ? " active" : ""}`}
                    onClick={() => setDiagTab("log")}
                  >
                    <Terminal size={14} /> Agent Log
                  </button>
                </div>

                {diagTab === "env" && (
                  <div className="diag-pane">
                    {diag.envError ? (
                      <p className="diag-error">{diag.envError}</p>
                    ) : diagEditing ? (
                      /* ── Edit mode ── */
                      <div className="env-edit-wrap">
                        <textarea
                          className="env-textarea"
                          value={diagDraft}
                          onChange={(e) => setDiagDraft(e.target.value)}
                          spellCheck={false}
                          autoComplete="off"
                          autoCorrect="off"
                          rows={Math.max(8, diagDraft.split("\n").length + 1)}
                        />

                        {/* Changed-key diff summary */}
                        {(envDiff.changed.length > 0 || envDiff.added.length > 0 || envDiff.removed.length > 0) && (
                          <div className="env-diff">
                            {envDiff.changed.map((k) => (
                              <span key={k} className="diff-tag diff-changed">~ {k}</span>
                            ))}
                            {envDiff.added.map((k) => (
                              <span key={k} className="diff-tag diff-added">+ {k}</span>
                            ))}
                            {envDiff.removed.map((k) => (
                              <span key={k} className="diff-tag diff-removed">− {k}</span>
                            ))}
                          </div>
                        )}

                        {/* Sensitive-key warning */}
                        {hasSensitiveChange && (
                          <div className="env-warning">
                            <AlertCircle size={14} />
                            <span>
                              You are changing <strong>AGENT_ID</strong> or <strong>TENANT_KEY</strong>.
                              This will re-register the agent under a new identity or tenant — make sure the values are correct before saving.
                            </span>
                          </div>
                        )}

                        {/* Restart toggle */}
                        <label className="env-restart-toggle">
                          <input
                            type="checkbox"
                            checked={diagRestartAfter}
                            onChange={(e) => setDiagRestartAfter(e.target.checked)}
                            disabled={diagSaving}
                          />
                          Restart agent after saving (recommended)
                        </label>

                        <div className="env-actions">
                          <button className="env-btn env-btn-save" onClick={saveEnv} disabled={diagSaving}>
                            {diagSaving ? <><RefreshCw size={13} className="animate-spin" /> Saving…</> : "Save Config"}
                          </button>
                          <button className="env-btn env-btn-cancel" onClick={cancelEdit} disabled={diagSaving}>
                            Cancel
                          </button>
                        </div>
                      </div>
                    ) : (
                      /* ── Read mode ── */
                      <>
                        <div className="env-read-header">
                          {!diag?.fromCache && (
                            <button className="env-btn env-btn-edit" onClick={startEdit}>
                              Edit Config
                            </button>
                          )}
                          {diag?.fromCache && (
                            <span style={{ fontSize: "0.75rem", color: "#f59e0b" }}>
                              Read-only — agent is offline
                            </span>
                          )}
                        </div>
                        <div className="diag-env">
                          {(diag.envContent || "").split("\n").map((line, i) => {
                            const trimmed = line.trim();
                            if (!trimmed || trimmed.startsWith("#")) {
                              return <div key={i} className="env-comment">{line}</div>;
                            }
                            const eq = line.indexOf("=");
                            if (eq === -1) return <div key={i}>{line}</div>;
                            const key = line.slice(0, eq);
                            const val = line.slice(eq + 1);
                            const isSensitive = /key|pass|secret|cred|token/i.test(key);
                            return (
                              <div key={i} className="env-row">
                                <span className="env-key">{key}</span>
                                <span className="env-eq">=</span>
                                <span className={`env-val${isSensitive ? " env-sensitive" : ""}`}>
                                  {isSensitive ? val.slice(0, 6) + "••••••••" : val}
                                </span>
                              </div>
                            );
                          })}
                        </div>
                      </>
                    )}
                  </div>
                )}

                {diagTab === "log" && (
                  <div className="diag-pane">
                    {diag.logError ? (
                      <p className="diag-error">{diag.logError}</p>
                    ) : (
                      <div className="diag-log">
                        {diag.logLines.map((line, i) => {
                          let cls = "log-info";
                          if (/\[ERROR\]/.test(line)) cls = "log-error";
                          else if (/\[WARNING\]/.test(line)) cls = "log-warn";
                          return <div key={i} className={`log-line ${cls}`}>{line}</div>;
                        })}
                      </div>
                    )}
                  </div>
                )}
              </>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default DeviceDetail;
